import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User,
  signInWithPopup,
  GoogleAuthProvider,
  getAdditionalUserInfo,
  signInWithEmailAndPassword,
  signInAnonymously,
  signOut,
  onAuthStateChanged,
  updateProfile,
  linkWithPopup,
  linkWithCredential,
  EmailAuthProvider,
  UserCredential,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from '../services/firebase';
import { createUserProfile, updateUserProfile, getUserProfile } from '../services/userService';
import { withRetry } from '@planacle/shared';
import { setSentryUser } from '../lib/sentry';
import { identifyUser, resetAnalytics, trackLogin, trackSignUp } from '../lib/analytics';
import { STORAGE_KEY as EVENT_DRAFT_STORAGE_KEY } from '../features/create-event/context/eventDraftConstants';
import { PREFS_DRAFT_KEY_PREFIX } from '../constants/storageKeys';

const ONBOARDING_CACHE_KEY = (uid: string) => `planacle_onboarding_done_${uid}`;

interface AuthContextType {
  user: User | null;
  loading: boolean;
  hasCompletedOnboarding: boolean;
  markOnboardingComplete: () => Promise<void>;
  signInWithGoogle: () => Promise<UserCredential>;
  signInWithEmail: (email: string, pass: string) => Promise<UserCredential>;
  signUpWithEmail: (
    name: string,
    email: string,
    pass: string,
    location?: { country: string; city: string; address?: string; postcode?: string }
  ) => Promise<UserCredential>;
  signUpClientSide: (email: string, pass: string, name: string) => Promise<UserCredential>;
  signInAsGuest: (name: string) => Promise<User>;
  signInAnonymouslyInBackground: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setSentryUser(currentUser?.uid ?? null);
      if (currentUser && !currentUser.isAnonymous) {
        identifyUser(currentUser.uid, {
          displayName: currentUser.displayName ?? '',
        });
      } else if (!currentUser) {
        resetAnalytics();
      }
      setLoading(false);

      if (currentUser && !currentUser.isAnonymous) {
        const cacheKey = ONBOARDING_CACHE_KEY(currentUser.uid);
        if (localStorage.getItem(cacheKey) === '1') {
          // Cache hit: trust it; skip Firestore read to avoid billing a read on every page load
          setHasCompletedOnboarding(true);
        } else {
          // H-425: reset before the async check — a direct account switch (user A → user B
          // with no intermediate null, e.g. sign-up while A is still signed in) must not
          // let B inherit A's stale `true` and skip onboarding.
          setHasCompletedOnboarding(false);
          // Cache miss: verify against Firestore as source of truth (new device / cleared storage)
          getUserProfile(currentUser.uid)
            .then((profile) => {
              // H-425: ignore stale resolutions — the signed-in user may have changed
              // while the profile read was in flight.
              if (profile?.hasCompletedOnboarding && auth.currentUser?.uid === currentUser.uid) {
                setHasCompletedOnboarding(true);
                localStorage.setItem(cacheKey, '1');
              }
            })
            .catch(() => {
              // Non-blocking — onboarding pages will show normally if Firestore is unreachable
            });
        }
      } else {
        setHasCompletedOnboarding(false);
      }
    });
    return unsubscribe;
  }, []);

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    // Anonymous → permanent upgrade: link the Google credential to the existing
    // anonymous account so the uid is preserved and any events/participant docs
    // created while anonymous stay accessible. Fall back to a fresh sign-in when
    // there is no anonymous session, or the Google account already belongs to
    // another user (credential-already-in-use) — then the permanent identity is
    // taken and orphaning the anonymous session is unavoidable.
    const current = auth.currentUser;
    if (current?.isAnonymous) {
      try {
        const linked = await linkWithPopup(current, provider);
        // Linking fires no auth onCreate trigger, so the users/{uid} doc would keep the
        // anonymous session's identity (email null, guest displayName) forever. Write the
        // Google identity client-side — mirrors the signUpClientSide link path.
        try {
          await createUserProfile(linked.user.uid, {
            email: linked.user.email,
            displayName: linked.user.displayName,
            photoURL: linked.user.photoURL,
          });
        } catch (e) {
          console.warn('[signInWithGoogle] createUserProfile failed after link', e);
        }
        setUser(linked.user);
        trackSignUp('google');
        return linked;
      } catch (err) {
        const code = (err as { code?: string }).code;
        if (code !== 'auth/credential-already-in-use' && code !== 'auth/email-already-in-use') {
          throw err;
        }
        // fall through to a fresh sign-in
      }
    }
    const credential = await signInWithPopup(auth, provider);
    if (getAdditionalUserInfo(credential)?.isNewUser) {
      // H-304-class race: a brand-new Google user's doc otherwise only exists after the
      // async onUserCreate trigger fires, so onboarding's immediate updateDoc can hit
      // NOT_FOUND. Write it synchronously; the trigger is idempotent and no-ops.
      try {
        await createUserProfile(credential.user.uid, {
          email: credential.user.email,
          displayName: credential.user.displayName,
          photoURL: credential.user.photoURL,
        });
      } catch (e) {
        console.warn('[signInWithGoogle] createUserProfile failed, falling back to trigger', e);
      }
      trackSignUp('google');
    } else {
      trackLogin('google');
    }
    setUser(credential.user);
    return credential;
  };

  const signInWithEmail = async (email: string, pass: string) => {
    const credential = await signInWithEmailAndPassword(auth, email, pass);
    setUser(credential.user);
    trackLogin('email');
    return credential;
  };

  const signUpClientSide = async (email: string, pass: string, name: string) => {
    // Anonymous → permanent upgrade: link the email/password credential to the
    // existing anonymous account (preserving the uid) instead of minting a new
    // user. Mirrors the server-side upgrade in the createAccount CF so the
    // emulator/E2E and CF-fallback paths don't orphan guest-created data.
    const current = auth.currentUser;
    if (current?.isAnonymous) {
      try {
        const linked = await linkWithCredential(current, EmailAuthProvider.credential(email, pass));
        await updateProfile(linked.user, { displayName: name });
        try {
          await createUserProfile(linked.user.uid, {
            email: linked.user.email,
            displayName: name,
            photoURL: linked.user.photoURL,
          });
        } catch (e) {
          console.warn('[signUpClientSide] createUserProfile failed after link', e);
        }
        setUser(linked.user);
        trackSignUp('email');
        return linked;
      } catch (err) {
        const code = (err as { code?: string }).code;
        if (code !== 'auth/credential-already-in-use' && code !== 'auth/email-already-in-use') {
          throw err;
        }
        // Credential already belongs to another account — fall through to a fresh
        // create (which will switch identity; orphaning the anon session is then
        // unavoidable because the permanent identity is taken).
      }
    }
    const { createUserWithEmailAndPassword } = await import('firebase/auth');
    // H-305: retry `createUserWithEmailAndPassword` on transient network failures
    // (`auth/network-request-failed`). The Firebase Auth emulator drops requests
    // under parallel-context load; without retry, E2E suites that signup multiple
    // users hit this ~1-in-3 runs. Only retry transient errors — logical errors
    // (already-exists, weak-password, invalid-email) propagate immediately so the
    // SignUp page can show the correct user-facing message instead of waiting.
    const credential = await withRetry(() => createUserWithEmailAndPassword(auth, email, pass), {
      retries: 2,
      backoff: 500,
      factor: 2,
      shouldRetry: (err) => {
        const code = (err as Error & { code?: string }).code;
        return code === 'auth/network-request-failed' || code === 'auth/too-many-requests';
      },
    });
    await updateProfile(credential.user, { displayName: name });
    // H-304: write the Firestore user doc synchronously so post-signup callers
    // (notably OnboardingPage → updateUserProfile) don't race with the async
    // onUserCreate trigger. The trigger is idempotent — when it fires it sees the
    // doc already exists and no-ops — so the two writers coexist safely.
    try {
      await createUserProfile(credential.user.uid, {
        email: credential.user.email,
        displayName: name,
        photoURL: credential.user.photoURL,
      });
    } catch (e) {
      // Non-fatal: trigger will create the doc shortly. Log and continue so signup
      // doesn't fail if Firestore rules transiently reject (e.g., rules deploy lag).
      console.warn('[signUpClientSide] createUserProfile failed, falling back to trigger', e);
    }
    setUser(credential.user);
    trackSignUp('email');
    return credential;
  };

  const signUpWithEmail = async (
    name: string,
    email: string,
    pass: string,
    location?: { country: string; city: string; address?: string; postcode?: string }
  ) => {
    // Optimization for E2E/Emulator: Use client-side signup directly or if it fails.
    const isEmulator =
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === 'localhost' ||
      auth.app.options.projectId?.includes('demo-');

    if (isEmulator) {
      return signUpClientSide(email, pass, name);
    }

    // BL-24: Replaced hand-rolled while loop with withRetry from @planacle/shared.
    // `already-exists` is treated as a success (idempotent) — swallowed before withRetry sees it.
    const createAccountFn = httpsCallable(functions, 'createAccount');
    try {
      await withRetry(
        async () => {
          try {
            await createAccountFn({ email, password: pass, displayName: name, location });
          } catch (err: unknown) {
            const e = err as { code?: string; message?: string };
            // Treat "already exists" as success — no point retrying
            if (
              e?.code === 'already-exists' ||
              e?.message?.includes('already in use') ||
              e?.message?.includes('already-exists')
            ) {
              return;
            }
            throw err;
          }
        },
        { retries: 2, backoff: 1500 } // 2 retries → 3 total attempts; 1 500 ms initial backoff (doubles each retry)
      );
    } catch (err: unknown) {
      const code = (err as { code?: string }).code;
      // M-454: when the CF was rate-limited or had an internal failure the account
      // was never created — skip the sign-in fallback (which would surface a
      // misleading "Incorrect email or password." error) and propagate the real error.
      if (code === 'functions/resource-exhausted' || code === 'functions/internal') {
        throw err;
      }
      const msg = err instanceof Error ? err.message : String(err);
      console.error(
        '[AuthContext] All createAccount attempts failed. Trying sign-in as fallback.',
        msg
      );
    }

    // BL-24: Sign-in with withRetry — replaces second hand-rolled while loop.
    // 4 retries = 5 total attempts; 1 000 ms initial backoff doubles each attempt.
    const credential = await withRetry(
      () => signInWithEmailAndPassword(auth, email, pass),
      { retries: 4, backoff: 1000 } // 4 retries → 5 total attempts; 1 000 ms initial backoff
    );
    setUser(credential.user);
    trackSignUp('email');
    return credential;
  };

  const signInAsGuest = async (name: string) => {
    const userCredential = await signInAnonymously(auth);
    await updateProfile(userCredential.user, { displayName: name });
    setUser(userCredential.user);
    return userCredential.user;
  };

  const signInAnonymouslyInBackground = async () => {
    if (user) return;
    // BL-12: Wrap in try/catch — this is a background operation; failure must not crash the app.
    // Downstream code must handle user===null gracefully.
    try {
      const userCredential = await signInAnonymously(auth);
      setUser(userCredential.user);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      console.error('[AuthContext] Anonymous sign-in failed:', msg);
      // Dispatch an event so UI components can optionally show a reconnect prompt
      window.dispatchEvent(
        new CustomEvent('auth:anonymous-sign-in-failed', { detail: { error: msg } })
      );
    }
  };

  const markOnboardingComplete = async () => {
    if (!user || user.isAnonymous) return;
    const cacheKey = ONBOARDING_CACHE_KEY(user.uid);
    // Update local state and cache immediately for instant UI response
    setHasCompletedOnboarding(true);
    localStorage.setItem(cacheKey, '1');
    // Persist to Firestore as source of truth (non-blocking on failure)
    try {
      await updateUserProfile(user.uid, { hasCompletedOnboarding: true });
    } catch {
      // Acceptable: localStorage cache covers this session; Firestore will sync on next login
    }
  };

  const logout = async () => {
    sessionStorage.clear();
    // Clear the onboarding cache for the current user so a subsequent login (same or different
    // user on this device) always re-validates against Firestore rather than inheriting stale state
    if (user && !user.isAnonymous) {
      localStorage.removeItem(ONBOARDING_CACHE_KEY(user.uid));
    }
    // H-425: drafts are keyed by feature/event, not uid — clear them so the next account
    // on this device doesn't see the previous user's event draft or preference drafts
    // (event name, dates, location, participant emails).
    localStorage.removeItem(EVENT_DRAFT_STORAGE_KEY);
    Object.keys(localStorage)
      .filter((key) => key.startsWith(PREFS_DRAFT_KEY_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
    await signOut(auth);
  };

  const value = {
    user,
    loading,
    hasCompletedOnboarding,
    markOnboardingComplete,
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signUpClientSide,
    signInAsGuest,
    signInAnonymouslyInBackground,
    logout,
  };

  // H-256: do not gate the entire app on auth loading — that prevents the Router from mounting
  // and blocks <ProtectedRoute>'s <Navigate> redirect from executing for unauthenticated users
  // visiting protected paths. ProtectedRoute shows its own spinner via the `loading` flag below.
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
